// ============================================================
// HOME PAGE — Unified data source for all home page components along with the placement track page, Footer and Navbar section content as well.
// All components should import from this file only
// ============================================================

// 
// HERO SECTION
// ============================================================
export const HERO = {
    badge: "India's AI-powered Learning + Hiring Ecosystem",
    headline: "India's first <br/>AI-powered B.Tech<br/>Ecosystem.",
    subheadline:
        "A transformative 4-year engineering experience to bridge the gap between textbook theory and undeniable technical mastery unlocking predictable, high-impact careers.",
    ctaPrimary: "Join the Waitlist",
    ctaSecondary: "Partner With Us",
    meta: "2,847+ students & 60+ companies already on the waitlist",
    audienceCards: [
        {
            key: "student",
            tag: "For Students",
            headline: "Your Degree. Supercharged with AI.",
            subcopy:
                "Build real projects. Earn verified skills. Get placed at companies that pay you what you're worth.",
            cta: "Join the Student Waitlist",
            accent: "from-aiblue to-blue-700",
            image: "https://images.unsplash.com/photo-1758270705172-07b53627dfcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
        },
        {
            key: "university",
            tag: "For Universities",
            headline: "Give your students the edge no traditional degree can.",
            subcopy:
                "AI.Prof integrates into your existing degree program no disruption, no overhaul. Just better outcomes. More placements. Happier students. Stronger institutional reputation.",
            cta: "Partner With AI.Prof",
            accent: "from-aiorange to-orange-600",
            image: `${process.env.PUBLIC_URL}/images/image.png`,
        },
        {
            key: "company",
            tag: "For Companies",
            headline: "Hire Smarter. Hire Faster. Hire AI-Ready.",
            subcopy:
                "Stop sifting through CVs. AI.Prof sends you pre-verified, project-backed candidates matched to your exact requirements.",
            cta: "Start Hiring From AI.Prof",
            accent: "from-emerald-500 to-teal-600",
            image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
        },
    ],
};

// ============================================================
// TRUSTED BY SECTION
// ============================================================
export const TRUSTED_COMPANIES = [
    {
        name: "Google",
        logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
    },
    {
        name: "Microsoft",
        logo: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg",
    },
    {
        name: "Amazon",
        logo: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
    },
    {
        name: "Meta",
        logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg",
    },
    {
        name: "Adobe",
        logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Adobe_Systems_logo_and_wordmark.svg",
    },
    {
        name: "IBM",
        logo: "https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg",
    },
    {
        name: "Oracle",
        logo: "https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg",
    },
    {
        name: "Salesforce",
        logo: "https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg",
    },
    {
        name: "Intel",
        logo: "https://upload.wikimedia.org/wikipedia/commons/7/7d/Intel_logo_%282006-2020%29.svg",
    },
    {
        name: "Cisco",
        logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Cisco_logo_blue_2016.svg",
    },
    {
        name: "SAP",
        logo: "https://upload.wikimedia.org/wikipedia/commons/5/59/SAP_2011_logo.svg",
    },
    {
        name: "Accenture",
        logo: "https://upload.wikimedia.org/wikipedia/commons/c/cd/Accenture.svg",
    },
    {
        name: "Deloitte",
        logo: "https://upload.wikimedia.org/wikipedia/commons/e/ed/Logo_of_Deloitte.svg",
    },
    {
        name: "Goldman Sachs",
        logo: "https://upload.wikimedia.org/wikipedia/commons/6/61/Goldman_Sachs.svg",
    },
    {
        name: "JPMorgan",
        logo: "https://upload.wikimedia.org/wikipedia/commons/2/20/JPMorgan_logo.svg",
    },
    {
        name: "Walmart",
        logo: "https://upload.wikimedia.org/wikipedia/commons/5/5b/Walmart_logo_%282025%29.svg",
    },
    {
        name: "Visa",
        logo: "https://upload.wikimedia.org/wikipedia/commons/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg",
    },
    {
        name: "Mastercard",
        logo: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
    },
];

export const TRUSTED_BY_SECTION = {
    tagline:
        "Built to Meet the Standards of the World's Best Engineering Teams",
};

// ============================================================
// STATS & QUOTE SECTION
// ============================================================
export const STATS_QUOTE_SECTION = {
    tag: "The Numbers That Drive Us",
    headline:
        "We're not building another EdTech. We're building infrastructure.",
    defaultStats: {
        students_waitlist: 2847,
        hiring_partners: 60,
        university_partners: 18,
    },
    quote: {
        text: "Every single student deserves an undeniable, unstoppable trajectory into the tech ecosystem. That isn't a passive mission statement on a wall, it is the exact engineering blueprint behind everything we do at AI.Prof.",
        author: "Sandeep Konam",
        role: "Founder & Chairman, AI.Prof",
        img: `${process.env.PUBLIC_URL}/general/sandeep_quote_img.png`,
    },
};

// ============================================================
// PROGRAMMES SECTION
// ============================================================
export const PROGRAMMES_SECTION = {
    tag: "Program",
    headline:
        'Learn what the industry <span className="text-aiblue">actually hires for.</span>',
    flagship: {
        tag: "Flagship Program",
        title: "Computer Science + AI (4-Year Program)",
        description:
            "A full undergraduate degree in Computer Science, redesigned with AI-integrated curriculum, real-world projects, AI assistant available 24/7, and placement support from Semester 1.",
        features: [
            "🎓︎ 4-year cs degree",
            "🌐︎ Partnered universities",
            "💼︎ Placement support included",
        ],
        cta: "Explore the Programme",
    },
    specializationsHeadline:
        'Choose your specialization.<br> <span className="text-aiblue">Every path leads to a future worth building.</span>',
    specializationsSubtext:
        "Built on a future-ready AI foundation, every specialization transforms ambition into skills, innovation, and real-world impact.",
    badgeMostPopular: "Most Popular",
    ctaJoinSpecialization: "Join the Specialization",
    ctaJoinWaitlist: "Join Waitlist",
    coursesSection: {
        tag: "Courses",
        headline:
            "Specialized tracks. Built for careers that shape the future.",
        cta: "Join Waitlist",
    },
};

// ── Specialization domains (for CS Programme cards) ─────────────────────────
export const CS_SPECIALIZATIONS = [
    {
        name: "Artificial Intelligence & Machine Learning",
        desc: "Architect the intelligence systems driving the global tech shift.",
        featured: true,
        about: "Master the complete AI stack from mathematical foundations to deep learning, NLP, and generative AI. Engineer models using TensorFlow and PyTorch, deploy to the cloud, and build a high-fidelity portfolio that proves you can build the future.",
    },
    {
        name: "Data Science",
        desc: "Translate complex data architectures into strategic execution.",
        featured: false,
        about: "Extract predictive insights from massive datasets using Python, SQL, and Spark. Master statistical modeling, machine learning pipelines, and advanced analytics to become the critical strategist every modern enterprise relies on.",
    },
    {
        name: "Cyber Security",
        desc: "Defend critical infrastructure against sophisticated threats.",
        featured: false,
        about: "Advance from core networking to rigorous penetration testing, malware analysis, and incident response. Master cryptography and cloud security, graduating prepared for elite engineering certifications like CEH and OSCP.",
    },
    {
        name: "Computer Science & Engineering",
        desc: "Core computational architecture, natively supercharged by AI.",
        featured: false,
        about: "Build an unshakeable foundation in algorithms, data structures, and distributed networks, supercharged with cloud computing and system design. The classic engineering degree, completely optimized with native AI from Semester 1.",
    },
    {
        name: "Information Technology",
        desc: "Scale enterprise platforms with modern cloud and DevOps.",
        featured: false,
        about: "Bridge the gap between enterprise scale and deep technical execution. Master full-stack development, multi-cloud management, and infrastructure-as-code paired with modern AI workflows to design resilient enterprise systems.",
    },
];


// ── Unified Courses Array (used for scrolling marquee) ──────────────────────
// All courses use the same rich card format with domain, tagline, tools, level, duration
export const COURSES = [
    // ── Bootcamp / Flagship Courses ──────────────────────────────────────
    {
        key: "fullstack",
        name: "Full Stack Web Development",
        tagline: "From idea to deployed product.",
        tools: ["React", "Node.js", "MongoDB", "AWS", "Docker", "GitHub"],
        outcome: "Build 5 production-grade projects.",
        level: "Beginner → Job-Ready",
        domain: "Full Stack",
        format: "Hybrid",
        duration: "24 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "ai",
        name: "AI & Machine Learning",
        tagline: "Don't just use AI. Build it.",
        tools: ["Python", "TensorFlow", "PyTorch", "HuggingFace", "LangChain"],
        outcome: "Build end-to-end ML models.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Live + Recorded",
        duration: "20 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "data",
        name: "Data Science & Analytics",
        tagline: "Turn raw data into decisions.",
        tools: ["Python", "SQL", "Pandas", "Power BI", "Tableau"],
        outcome: "Build a data portfolio companies review.",
        level: "Beginner → Intermediate",
        domain: "Data Science",
        format: "Hybrid",
        duration: "18 weeks",
        bg: "bg-aimint",
    },
    {
        key: "cloud",
        name: "Cloud Engineering & DevOps",
        tagline: "Make every product run perfectly.",
        tools: ["AWS", "Kubernetes", "Terraform", "CI/CD", "Linux"],
        outcome: "Earn cloud certifications. Deploy real infra.",
        level: "Intermediate",
        domain: "Cloud",
        format: "Live + Recorded",
        duration: "20 weeks",
        bg: "bg-aisky",
    },
    {
        key: "cyber",
        name: "Cybersecurity & Ethical Hacking",
        tagline: "Protect what others build.",
        tools: ["Kali Linux", "Wireshark", "Metasploit", "OWASP"],
        outcome: "Earn CEH-aligned certifications.",
        level: "Beginner → Advanced",
        domain: "Cybersecurity",
        format: "Hybrid",
        duration: "22 weeks",
        bg: "bg-aibeige",
    },

    // ── Foundation & Core Programming ────────────────────────────────────
    {
        key: "python-prog",
        name: "Python Programming",
        tagline: "Master the industry-standard language for data and AI.",
        tools: ["Python", "OOP", "Data Structures", "Clean Code"],
        outcome: "Write production-ready Python code.",
        level: "Beginner → Intermediate",
        domain: "Programming",
        format: "Self-Paced",
        duration: "10 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "math-ai",
        name: "Math Foundations for AI",
        tagline: "Build the mathematical bedrock for machine learning.",
        tools: ["Linear Algebra", "Calculus", "Matrices", "NumPy"],
        outcome: "Grasp the math that powers all AI models.",
        level: "Beginner",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "stats-ai",
        name: "Statistical Methods for AI",
        tagline:
            "Learn the statistics that drive data analysis and model training.",
        tools: ["Probability", "Hypothesis Testing", "Regression", "SciPy"],
        outcome: "Analyze data behavior and train models with confidence.",
        level: "Beginner → Intermediate",
        domain: "Data Science",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aimint",
    },
    {
        key: "ml-lifecycle",
        name: "Problem Solving & ML Life Cycle",
        tagline:
            "Master the end-to-end lifecycle of a machine learning project.",
        tools: [
            "Problem Scoping",
            "Data Pipelines",
            "Deployment",
            "Monitoring",
        ],
        outcome: "Take an ML project from idea to production.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "6 weeks",
        bg: "bg-aisky",
    },

    // ── Data Engineering & Management ────────────────────────────────────
    {
        key: "data-viz",
        name: "Transforming Data: Visualization & Processing",
        tagline: "Master the art of data wrangling and visualization.",
        tools: ["Python", "Pandas", "Matplotlib", "Seaborn", "Plotly"],
        outcome: "Clean raw data and build impactful visual charts.",
        level: "Beginner → Intermediate",
        domain: "Data Science",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "data-annotation",
        name: "Data Annotation for AI",
        tagline: "Learn the foundational workflows of labeling data for AI.",
        tools: [
            "Labeling Tools",
            "Text Annotation",
            "Image Labeling",
            "Quality Control",
        ],
        outcome: "Prepare high-quality datasets for supervised learning.",
        level: "Beginner",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "6 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "data-mgmt",
        name: "Data Management for AI",
        tagline: "Structure, store, and govern data assets efficiently.",
        tools: ["SQL", "Data Governance", "Privacy", "Data Quality"],
        outcome: "Ensure data quality and compliance across AI pipelines.",
        level: "Intermediate",
        domain: "Data Science",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "big-data",
        name: "Data Engineering & Big Data",
        tagline: "Dive into distributed computing and massive data processing.",
        tools: ["Spark", "Hadoop", "ETL", "Data Warehousing"],
        outcome: "Process high-velocity datasets at scale.",
        level: "Advanced",
        domain: "Data Science",
        format: "Live + Recorded",
        duration: "12 weeks",
        bg: "bg-aimint",
    },

    // ── Core AI, ML & Deep Learning ──────────────────────────────────────
    {
        key: "ai-intro",
        name: "Artificial Intelligence",
        tagline: "Explore the field that drives intelligent machines.",
        tools: [
            "Search Algorithms",
            "Knowledge Representation",
            "Reasoning",
            "Planning",
        ],
        outcome: "Understand the core philosophies and systems of AI.",
        level: "Beginner",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aisky",
    },
    {
        key: "ml-core",
        name: "Machine Learning",
        tagline: "Master supervised and unsupervised learning algorithms.",
        tools: [
            "Scikit-Learn",
            "Decision Trees",
            "SVM",
            "Clustering",
            "Ensemble",
        ],
        outcome: "Build models that learn from data.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "deep-learning",
        name: "Deep Learning",
        tagline: "Go beyond shallow models into multi-layer neural networks.",
        tools: [
            "Neural Networks",
            "Backpropagation",
            "CNNs",
            "RNNs",
            "Regularization",
        ],
        outcome: "Build architectures that mimic the human brain.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "14 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "tf-pytorch",
        name: "Mastering TensorFlow & PyTorch",
        tagline:
            "Get rigorous hands-on with the two most popular DL frameworks.",
        tools: ["TensorFlow", "PyTorch", "Keras", "Model Debugging"],
        outcome: "Build, train, and debug complex neural networks.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Live + Recorded",
        duration: "10 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "algo-opt",
        name: "Algorithms & Optimization in AI",
        tagline: "Deep dive into the math that makes AI training precise.",
        tools: [
            "Gradient Descent",
            "Evolutionary Algorithms",
            "Convex Optimization",
            "NumPy",
        ],
        outcome: "Optimize models for speed and accuracy.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aimint",
    },
    {
        key: "mlops",
        name: "ML Ops",
        tagline: "Bridge the gap between development and production.",
        tools: [
            "Docker",
            "Kubernetes",
            "CI/CD",
            "Model Versioning",
            "Monitoring",
        ],
        outcome: "Automate model deployment, scaling, and monitoring.",
        level: "Advanced",
        domain: "Cloud",
        format: "Live + Recorded",
        duration: "10 weeks",
        bg: "bg-aisky",
    },

    // ── Advanced AI & Modern Architectures ───────────────────────────────
    {
        key: "gen-ai",
        name: "Generative AI",
        tagline: "Explore the mechanics behind AI that creates.",
        tools: ["VAEs", "GANs", "Diffusion Models", "Synthetic Data"],
        outcome: "Build models that generate new content from scratch.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "transformers",
        name: "Transformers",
        tagline: "Master the architecture that revolutionized modern AI.",
        tools: [
            "Self-Attention",
            "Encoders",
            "Decoders",
            "Positional Encoding",
        ],
        outcome: "Understand the backbone of every modern LLM.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "llms",
        name: "LLMs / VLMs",
        tagline:
            "Explore the engineering behind Large Language and Vision Models.",
        tools: ["GPT", "BERT", "Vision Transformers", "HuggingFace"],
        outcome: "Understand how models process text and imagery.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Live + Recorded",
        duration: "10 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "reasoning",
        name: "Reasoning Models",
        tagline: "Study architectures that mimic human-like logical deduction.",
        tools: ["Chain-of-Thought", "Multi-Step Reasoning", "Logic Engines"],
        outcome: "Build AI that solves complex problems step by step.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-aimint",
    },
    {
        key: "prompt-eng",
        name: "Mastering Prompt Engineering",
        tagline: "Learn the art and science of guiding LLMs effectively.",
        tools: ["Zero-Shot", "Few-Shot", "Chain Prompting", "Frameworks"],
        outcome: "Maximize LLM output accuracy with precision inputs.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "6 weeks",
        bg: "bg-aisky",
    },
    {
        key: "latest-ai",
        name: "Latest Advancements in AI",
        tagline:
            "Stay on the frontier with cutting-edge research and breakthroughs.",
        tools: ["Research Papers", "Breakthrough Models", "Emerging Paradigms"],
        outcome: "Understand what's next in the global AI landscape.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "multimodal",
        name: "Multimodal AI Systems",
        tagline:
            "Build systems that process text, audio, video, and sensory data.",
        tools: [
            "Text + Image Models",
            "Audio Processing",
            "Video Understanding",
            "Sensor Fusion",
        ],
        outcome: "Create AI that crosses data stream boundaries.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "reinforcement",
        name: "Deep Reinforcement Learning",
        tagline: "Study reward-based learning for autonomous systems.",
        tools: [
            "Q-Learning",
            "Policy Gradients",
            "Agent-Environment",
            "OpenAI Gym",
        ],
        outcome: "Build AI agents that learn through trial and error.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "time-series",
        name: "Time Series Forecasting",
        tagline: "Master algorithms for sequential, time-ordered predictions.",
        tools: ["ARIMA", "LSTM", "Prophet", "Seasonal Decomposition"],
        outcome: "Predict trends in finance, logistics, and demand.",
        level: "Advanced",
        domain: "Data Science",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-aimint",
    },
    {
        key: "ai-case-studies",
        name: "Real-World AI Case Studies",
        tagline:
            "Analyze production-grade AI rollouts across global tech giants.",
        tools: [
            "Architecture Review",
            "Scaling Analysis",
            "Failure Modes",
            "Success Patterns",
        ],
        outcome: "Learn from real successes and failures at scale.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "6 weeks",
        bg: "bg-aisky",
    },
    {
        key: "ai-research",
        name: "Advanced AI Research",
        tagline:
            "Learn to read literature, formulate hypotheses, and write papers.",
        tools: [
            "Literature Review",
            "Hypothesis Design",
            "Experimentation",
            "Academic Writing",
        ],
        outcome: "Contribute original research to the AI community.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "12 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "web-api-ai",
        name: "Web API Integration for AI & LLMs",
        tagline: "Wrap AI models into production-ready APIs and workflows.",
        tools: ["FastAPI", "Flask", "REST APIs", "Webhooks", "Tool Calling"],
        outcome: "Deploy LLMs as live, callable services.",
        level: "Intermediate",
        domain: "Full Stack",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "explainable-ai",
        name: "Explainable AI",
        tagline:
            "Demystify the black box of deep learning with interpretation techniques.",
        tools: ["SHAP", "LIME", "Feature Attribution", "Model Auditing"],
        outcome: "Make AI decisions transparent, ethical, and auditable.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aipeach",
    },

    // ── Perception & Interaction Systems ─────────────────────────────────
    {
        key: "speech-tech",
        name: "Speech Technology Systems",
        tagline: "Master the mechanics of speech recognition and synthesis.",
        tools: ["ASR", "TTS", "Whisper", "WaveNet", "SpeechBrain"],
        outcome: "Build systems that understand and generate spoken language.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aimint",
    },
    {
        key: "nlp",
        name: "Natural Language Processing",
        tagline: "Help computers interpret and understand human language.",
        tools: ["Tokenization", "NER", "Sentiment Analysis", "spaCy", "NLTK"],
        outcome: "Build apps that read, understand, and respond to text.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-aisky",
    },
    {
        key: "computer-vision",
        name: "Computer Vision",
        tagline: "Teach machines to see and interpret the visual world.",
        tools: ["OpenCV", "CNNs", "Object Detection", "Segmentation", "YOLO"],
        outcome: "Build systems that detect, classify, and track objects.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "visual-intel",
        name: "Visual Intelligence",
        tagline: "Go beyond basic vision into deep spatial awareness.",
        tools: [
            "Multi-Object Tracking",
            "Scene Reconstruction",
            "3D Vision",
            "Depth Estimation",
        ],
        outcome: "Understand dynamic video streams and spatial contexts.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "robotics",
        name: "Robotics",
        tagline: "Where AI meets physical hardware and autonomous machines.",
        tools: [
            "ROS",
            "Kinematics",
            "Path Planning",
            "Sensor Fusion",
            "Actuators",
        ],
        outcome: "Build and program autonomous robotic systems.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Live + Recorded",
        duration: "14 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "conversational-ai",
        name: "Conversational AI",
        tagline: "Design and build intelligent voice agents and chatbots.",
        tools: [
            "DialogFlow",
            "Rasa",
            "Intent Recognition",
            "Dialogue Management",
        ],
        outcome: "Create agents that converse naturally with users.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aimint",
    },
    {
        key: "cg-3d",
        name: "Computer Graphics / 3D",
        tagline: "Explore AI-powered 3D rendering and neural graphics.",
        tools: ["Neural Rendering", "3D Generation", "Ray Tracing", "Blender"],
        outcome: "Generate and manipulate 3D assets with AI.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aisky",
    },

    // ── Healthcare & Medicine ────────────────────────────────────────────
    {
        key: "ai-healthcare",
        name: "Intro to AI in Healthcare",
        tagline: "Apply computer vision, NLP, and analytics to medicine.",
        tools: [
            "Medical Imaging",
            "Clinical NLP",
            "Predictive Analytics",
            "DICOM",
        ],
        outcome: "Understand how AI is transforming patient diagnostics.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "ai-clinical",
        name: "AI in Clinical Specialties & Patient Care",
        tagline: "Deploy AI tools across cardiology, radiology, and oncology.",
        tools: [
            "Radiology AI",
            "Cardiology Models",
            "AI Scribes",
            "Clinical Workflows",
        ],
        outcome: "Integrate AI into daily clinical practice.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "ai-public-health",
        name: "AI in Public Health & Epidemiology",
        tagline: "Track disease outbreaks and model population health.",
        tools: ["Epi Models", "Outbreak Tracking", "Population Health", "GIS"],
        outcome: "Use ML to optimize healthcare resource allocation.",
        level: "Intermediate",
        domain: "Data Science",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "ai-hospital",
        name: "AI in Hospital Management",
        tagline: "Optimize hospital operations with predictive AI.",
        tools: ["Patient Flow", "Bed Management", "Billing AI", "Scheduling"],
        outcome: "Streamline operations and reduce patient wait times.",
        level: "Intermediate",
        domain: "Data Science",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-aimint",
    },
    {
        key: "ai-mental-health",
        name: "AI in Mental Health & Well-Being",
        tagline: "Use NLP and conversational agents for therapeutic support.",
        tools: ["Sentiment Monitoring", "Chatbots", "Early Diagnostics", "NLP"],
        outcome: "Build accessible mental health support systems.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "8 weeks",
        bg: "bg-aisky",
    },
    {
        key: "ai-drug-discovery",
        name: "AI in Medical Research & Drug Discovery",
        tagline: "Speed up the discovery lifecycle for new medications.",
        tools: [
            "Molecular Modeling",
            "Compound Screening",
            "Deep Learning",
            "Bioinformatics",
        ],
        outcome: "Predict molecular structures and shorten R&D cycles.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "ai-biotech",
        name: "AI in Biotechnology & Genetic Engineering",
        tagline: "Apply deep learning to genomics and CRISPR optimization.",
        tools: ["Genomic Sequencing", "CRISPR", "Gene Expression", "BioPython"],
        outcome: "Model and engineer biological systems with AI.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-ailavender",
    },

    // ── Creative Arts & Entertainment ────────────────────────────────────
    {
        key: "ai-vfx",
        name: "AI for VFX & Motion Graphics",
        tagline:
            "Supercharge visual effects with neural rendering and deep learning.",
        tools: ["Neural Rendering", "Rotoscoping", "Green Screen", "DL Tools"],
        outcome: "Automate and enhance VFX pipelines with AI.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aipeach",
    },
    {
        key: "ai-game-dev",
        name: "AI in Game Development",
        tagline:
            "Reshape game design with procedural generation and adaptive AI.",
        tools: [
            "Procedural Generation",
            "NPC Behavior",
            "AI Texturing",
            "Unity/Unreal",
        ],
        outcome: "Create smarter, more dynamic game experiences.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aimint",
    },
    {
        key: "ai-animation",
        name: "AI for Animation",
        tagline: "Automate keyframing, rigging, and motion simulation.",
        tools: [
            "Motion Capture AI",
            "Keyframe Automation",
            "Rigging",
            "2D/3D Sim",
        ],
        outcome: "Speed up animation workflows with machine learning.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "10 weeks",
        bg: "bg-aisky",
    },
    {
        key: "ai-xr",
        name: "AI for Extended Reality (AR/VR/MR)",
        tagline: "Power immersive interactions with spatial AI.",
        tools: [
            "Hand Tracking",
            "Environment Mapping",
            "Spatial AI",
            "Unity XR",
        ],
        outcome: "Build AR/VR experiences driven by computer vision.",
        level: "Advanced",
        domain: "AI/ML",
        format: "Hybrid",
        duration: "12 weeks",
        bg: "bg-aibeige",
    },
    {
        key: "ai-music",
        name: "AI for Music Composition & Production",
        tagline: "Generate original melodies and assist sound engineers.",
        tools: [
            "Generative Audio",
            "MusicGen",
            "Instrument Isolation",
            "DAW Plugins",
        ],
        outcome: "Compose and produce music with AI collaborators.",
        level: "Intermediate",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "8 weeks",
        bg: "bg-ailavender",
    },
    {
        key: "ai-storytelling",
        name: "AI for Narrative & Storytelling",
        tagline: "Collaborate with LLMs for character and plot development.",
        tools: [
            "LLMs",
            "Character Development",
            "Plot Mapping",
            "Script Formatting",
        ],
        outcome: "Design interactive branching narratives with AI.",
        level: "Beginner → Intermediate",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "6 weeks",
        bg: "bg-aipeach",
    },

    // ── Governance & Career ──────────────────────────────────────────────
    {
        key: "ai-governance",
        name: "Intro to AI in Governance",
        tagline:
            "Study how public sectors leverage AI for policy and services.",
        tools: ["Fraud Detection", "Policy AI", "Citizen Portals", "Ethics"],
        outcome: "Understand AI's role in public administration.",
        level: "Beginner",
        domain: "AI/ML",
        format: "Self-Paced",
        duration: "6 weeks",
        bg: "bg-aimint",
    },
    {
        key: "resume-cv",
        name: "Resume & CV Building",
        tagline: "Present your portfolio and beat recruiter screening systems.",
        tools: [
            "GitHub Portfolio",
            "Skill Metrics",
            "ATS Optimization",
            "LinkedIn",
        ],
        outcome:
            "Build a resume that gets past filters and impresses recruiters.",
        level: "Beginner",
        domain: "Career",
        format: "Self-Paced",
        duration: "4 weeks",
        bg: "bg-aisky",
    },
];

// Keep COURSE_CATALOG as alias for backward compatibility
// (Other components may import it)
export const COURSE_CATALOG = COURSES;

// ============================================================
// SKILL GAP SECTION
// ============================================================
export const SKILL_GAP_SECTION = {
    badge: "The Skill Gap",
    headline1: "India produces 1.5M engineers every year.",
    headline2: "Less than 20% are industry-ready.",
    description:
        'This is not a student problem. It\'s a <strong className="text-aiorange">system problem</strong>. AI.Prof was built to solve this.',
    whatCompaniesWant: [
        "Hands-on coding & deployment experience",
        "Real GitHub repositories & project history",
        "AI/ML awareness with practical exposure",
        "Problem-solving under real constraints",
        "Communication & collaboration skills",
    ],
    whatCollegesDeliver: [
        "Theory-heavy curriculum, limited lab time",
        "Assignments graded for marks, not mastery",
        "Subjects 3–5 years behind industry trends",
        "Placement support only in final year",
        "No verified proof of student skills",
    ],
    solution: {
        badge: "The AI.Prof Solution",
        text: "AI.Prof bridges this gap not just with better learning, but with a full ecosystem: AI-powered learning, real project portfolios, skill-tracking dashboards for universities, and an AI hiring pipeline that connects students directly to companies.",
    },
};

// ============================================================
// COMPARISON TABLE SECTION
// ============================================================
export const COMPARISON_SECTION = {
    tag: "What AI.Prof Does",
    headline:
        'You don\'t need another course. <br />You need a <span className="text-aiblue">system that connects <span className="text-aiorange">learning to your dream jobs.</span></span>',
    columnLabels: {
        dimension: "Dimension",
        traditional: "Traditional College",
        aiprof: "AI.Prof Solution",
    },
    rows: [
        ["Curriculum", "Outdated textbooks", "AI-updated, industry-aligned"],
        ["Learning Style", "Sit, listen, memorize", "Build & code every class"],
        [
            "Mentorship",
            "1 professor for 100 students",
            "Veda AI — 24/7 personal tutor",
        ],
        ["Projects", "Made-up exam assignments", "40+ real industry problems"],
        ["Tools Used", "Theory & rote learning", "GitHub, Cloud, AI systems"],
        ["Assessment", "MCQs, easy to copy", "AI dialogue-based vivas"],
        ["Feedback", "Weeks later, if at all", "Instant, automated evaluation"],
        [
            "Proof of Skills",
            "A degree certificate",
            "Verified digital portfolio",
        ],
        ["Career Support", "Maybe in final year", "From Semester 1"],
        ["Placements", "Only a few companies", "1800+ company network"],
        ["Faculty", "Same content, every year", "AI-upskilled, industry-fresh"],
    ],
    bottomQuote: {
        label: "A Choice",
        heading: "Don't leave your career to luck.",
        subheading1: "Eliminate the gamble.",
        subheading2: "Engineer the outcome.",
        description:
            'Build undeniable, verified proof from <span className="text-aiorange font-bold">Semester 1</span>.',
    },
};

// ── Simplified table rows for the new 2-column comparison table ────────────
// Each row: { dimension, traditional, aiprof }
export const TABLE_ROWS = COMPARISON_SECTION.rows.map(
    ([dimension, traditional, aiprof]) => ({
        dimension,
        traditional,
        aiprof,
    }),
);



// ============================================================
// MENTORS SECTION
// ============================================================
export const MENTORS_SECTION = {
    tag: "Learn From Industry Leaders",
    headline:
        'Learn from engineers building the future at the world’s <span className="text-aiblue">top technology companies.</span>',
    description:
        "Gain real-world insights, mentorship, and career guidance from engineers who have built products used by millions across the globe.",
    // cardFooter: "Live Mentorship · Career Guidance · Portfolio Reviews",
};

export const MENTORS = [
    {
        name: "Sania Alex",
        role: "Senior Software Engineer",
        company: "Google",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Sania_Alex.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Google.png`,
        linkedin: "https://www.linkedin.com/in/saniaalex/",
    },
    {
        name: "Akshay Kumar",
        role: "Senior SDE",
        company: "Amazon",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Akshay_Kumar.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Amazon.png`,
        linkedin: "https://www.linkedin.com/in/akshaypavan/",
    },
    {
        name: "Vishnu Rudrasamudram",
        role: "Senior Software Engineer - Robot Controls",
        company: "Symbotic",
        domain: "Robotics & AI",
        img: `${process.env.PUBLIC_URL}/mentors/Vishnu_Rudrasamudram.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/Symbotic.png`,
        linkedin: "https://www.linkedin.com/in/vishnuvrs/",
    },
    {
        name: "Sandeep Lamichhane",
        role: "Senior Staff Software Engineer",
        company: "Oracle",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Sandeep_Lamicchane.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Oracle.png`,
        linkedin: "https://www.linkedin.com/in/sandeep-lamichhane-8b700489/",
    },
    {
        name: "Madan Mohan Kuruba",
        role: "Software Engineer II",
        company: "Microsoft",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Madan_Mohan_Kuruba.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Microsoft.png`,
        linkedin: "https://www.linkedin.com/in/madan-mohan-kuruba/",
    },
    {
        name: "Susmitha Kandula",
        role: "Senior Software Engineer",
        company: "Rivian",
        domain: "Embedded Systems",
        img: `${process.env.PUBLIC_URL}/mentors/Susmitha_Kandula.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Rivian.png`,
        linkedin: "https://www.linkedin.com/in/kandulsh5/",
    },
    {
        name: "Indira Emmadi",
        role: "VP - Information Security & Technology",
        company: "State Street",
        domain: "Cybersecurity",
        img: `${process.env.PUBLIC_URL}/mentors/Indira_Emmadi.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/State Street.png`,
        linkedin: "https://www.linkedin.com/in/indira-e-721a5a9/",
    },
    {
        name: "Dinesh Katakam",
        role: "Software Engineer II",
        company: "Microsoft",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Dinesh_Katakam.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Microsoft.png`,
        linkedin: "https://www.linkedin.com/in/dineshkatakam/",
    },
    {
        name: "Tanvi Pareek",
        role: "Software Engineer",
        company: "Meta",
        domain: "Machine Learning",
        img: `${process.env.PUBLIC_URL}/mentors/tanvi_pareek.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/meta.png`,
        linkedin: "https://www.linkedin.com/in/tanvipareek/",
    },
    {
        name: "Ravali Bojja",
        role: "Senior Software Engineer",
        company: "Intuit",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Ravali_Bojja.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Intuit.png`,
        linkedin: "https://www.linkedin.com/in/ravalivr7/",
    },
    {
        name: "Tom Peter",
        role: "Software Engineer - Networking & Storage",
        company: "NetApp",
        domain: "Cloud / Networking",
        img: `${process.env.PUBLIC_URL}/mentors/Tom_Peter.jpeg`,
        logo: `${process.env.PUBLIC_URL}/mentors/Netapp.png`,
        linkedin: "https://www.linkedin.com/in/tompeterv10/",
    },
    {
        name: "Hari Krishna",
        role: "Staff Software Engineer",
        company: "Rippling",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Hari_Krishna.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/Rippling.png`,
        linkedin: "",
    },
    {
        name: "Sandeep Manchikalapudi",
        role: "Application Engineering Manager",
        company: "C3 AI",
        domain: "AI/ML",
        img: `${process.env.PUBLIC_URL}/mentors/sandeep_manchikalapudi.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/c3AI.png`,
        linkedin: "",
    },
    {
        name: "Sathish R",
        role: "Software Engineer",
        company: "Salesforce",
        domain: "Full Stack",
        img: `${process.env.PUBLIC_URL}/mentors/Sathish_r.png`,
        logo: `${process.env.PUBLIC_URL}/mentors/salesforce.png`,
        linkedin: "",
    },
];

// ============================================================
// FOOTER SECTION
// ============================================================
export const FOOTER_SECTION = {
    tagline:
        'AI Prof Pvt. Ltd is an AI-powered learning and hiring ecosystem connecting students, universities, and companies across India. <span className="text-aiorange font-medium">Building India\'s AI-Ready Tech Workforce.</span>',
    getInTouch: "Get In Touch",
    email: "support@aiprof.com",
    phone: "+918071580580",
    location: "Made in Hyderabad, India 🇮🇳",
    copyright: "© 2026 AI.Prof. All rights reserved.",
    columns: [
        {
            title: "Platform",
            links: [
                ["4-Year CS Program", "/cs-programme"],
                ["Placement Tracks", "/placement-tracks"],
                ["All Courses", "/#courses"],
                ["CS Programme FAQ", "/cs-programme#faq"],
                ["Placement Tracks FAQ", "/placement-tracks#faq"],
            ],
        },
        {
            title: "Company",
            links: [
                ["Leadership", "/about"],
                ["Careers", "/careers"],
            ],
        },
        {
            title: "Legal",
            links: [
                ["Privacy Policy", "/legal/privacy"],
                ["Cookie Policy", "/legal/cookies"],
                ["Terms of Service", "/legal/terms"],
            ],
        },
    ],
};

// ============================================================
// NAVIGATION (NAVBAR )
// ============================================================
export const NAVIGATION = [
    { label: "Home", to: "/" },
    {
        label: "Programs",
        isDropdown: true,
        dropdown: [
            { label: "Btech Computer Science with Specialization in AI & ML", to: "/cs-programme" },
            { label: "Placement Tracks", to: "/placement-tracks" },
        ],
    },
    { label: "Leadership", to: "/about" },
    { label: "Careers", to: "/careers" },
];
export const NAV_CTA_LABEL = "Join the Waitlist";
export const NAV_CTA_ROLES = [
    { label: "🎓 Student", role: "student" },
    { label: "🏛️ University", role: "university" },
    { label: "🏢 Recruiter", role: "company" },
];
// ============================================================
// PLACEMENT TRACKS
// ============================================================
export const PLACEMENT_TRACKS_PAGE = {
    hero: {
        badge: "Career Preparation Tracks",
        headline:
            'From learning to hiring. <span className="text-aiblue">Built to make students industry-ready.</span>',
        description:
            "Great careers are built on more than academic scores. Modern companies look for problem-solving ability, technical confidence, communication skills, and real execution. These Placement Tracks are designed to help students build the mindset, skills, and interview readiness required to succeed in today’s hiring landscape.",
    },

    tracks: [
        {
            key: "problem-solving",
            icon: "Brain",
            badge: "APTITUDE & REASONING",
            title: "Aptitude & Reasoning",
            tagline: "Build the analytical thinking companies look for.",
            highlights: [
                "Quantitative aptitude, logical reasoning, and analytical thinking",
                "Real-world problem-solving approaches used in technical hiring",
                "Practice assessments designed to improve speed, accuracy, and confidence",
                "Structured preparation for placement tests, competitive screenings, and interview rounds",
            ],
            cta: "Explore Problem Solving",
            image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=2670&auto=format&fit=crop",
            gradient: "from-purple-500/10 via-purple-50 to-white",
            accent: "bg-purple-500",
            border: "border-purple-200",
        },
        {
            key: "programming",
            icon: "Code",
            badge: "PROGRAMMING",
            title: "Programming with Python & Java",
            tagline:
                "Build strong programming foundations for modern tech careers.",
            highlights: [
                "Master core programming concepts using Python and Java",
                "Learn object-oriented programming, problem-solving, and application development",
                "Build real-world projects while improving coding logic and debugging skills",
                "Develop the technical confidence required for software engineering and technical interviews",
            ],
            cta: "Start Programming Track",
            image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=2672&auto=format&fit=crop",
            gradient: "from-aiblue/10 via-blue-50 to-white",
            accent: "bg-aiblue",
            border: "border-aiblue/30",
        },

        {
            key: "dsa",
            icon: "Database",
            badge: "DSA",
            title: "Data Structures & Algorithms",
            tagline:
                "Master DSA with Python & Java — the languages of top tech.",
            highlights: [
                "Master arrays, trees, graphs, recursion, DP with Python & Java implementations",
                "Develop strong computational thinking through structured problem-solving",
                "Practice interview-focused coding challenges with increasing complexity",
                "Learn how efficient systems and scalable applications are designed in the real world",
            ],
            cta: "Master DSA",
            image: "https://images.unsplash.com/photo-1516116216624-53e697fedbea?q=80&w=2728&auto=format&fit=crop",
            gradient: "from-aiorange/10 via-orange-50 to-white",
            accent: "bg-aiorange",
            border: "border-aiorange/30",
        },
    ],

    statsSection: {
        tag: "Why It Matters",
        headline:
            'Companies hire students who can <span className="text-aiblue">think, build, and communicate.</span>',
        cards: [
            {
                num: "Real",
                label: "Projects that demonstrate practical skills and execution",
                bg: "bg-ailavender",
                border: "border-ailavender",
                text: "text-aiblue",
            },
            {
                num: "Technical",
                label: "Interview preparation focused on coding, problem-solving, and systems thinking",
                bg: "bg-aipeach",
                border: "border-aipeach",
                text: "text-aiorange",
            },
            {
                num: "Career",
                label: "Guidance designed to help students become industry-ready with confidence",
                bg: "bg-aisky",
                border: "border-aisky",
                text: "text-aiblue",
            },
        ],
    },

    roadmapSection: {
        tag: "The Journey",
        headline:
            'A structured path from <span className="text-aiblue">learning to placement readiness.</span>',
        steps: [
            {
                title: "Build Strong Foundations",
                desc: "Develop core programming, reasoning, and technical problem-solving abilities.",
            },
            {
                title: "Practice Through Real Challenges",
                desc: "Apply concepts through coding exercises, assessments, projects, and interview-style tasks.",
            },
            {
                title: "Strengthen Interview Confidence",
                desc: "Prepare with mock interviews, communication training, and guided career preparation.",
            },
            {
                title: "Become Industry-Ready",
                desc: "Graduate with practical experience, verified skills, and the confidence to pursue top opportunities.",
            },
        ],
    },

    faq: {
        tag: "Frequently Asked",
        headline:
            'Your Questions, <span className="text-aiblue">Answered</span>',
    },
};

// ============================================================
// LEAD CAPTURE MODAL (Modal Popup in the home page after 40 sec)
// ============================================================
export const LEAD_MODAL = {
    uniProgrammes: [
        { key: "placement", label: "Placement Track" },
        { key: "specialization", label: "CS + AI 4 Year Track" },
        { key: "other", label: "Other" },
    ],
    roles: [
        { key: "student", icon: "🎓", label: "I'm a Student", desc: "Build skills & get placed" },
        { key: "university", icon: "🏛️", label: "I'm from a University", desc: "Partner with AI.Prof" },
    ],
    mobileStep: {
        headingPrefix: "Get Started with ",
        headingBrand: "AI.Prof",
        subheading: "Enter your mobile number to continue",
        countryCode: "+91",
        placeholder: "10-digit number",
    },
    roleStep: {
        heading: "Who are you?",
    },
    student: {
        eyebrows: {
            name: "Student · 1 of 2",
            email: "Student · 2 of 2",
        },
        headings: {
            name: "What's your name?",
            btech: "Are you looking to join the B.Tech program?",
        },
        placeholders: {
            name: "Your full name",
            email: "you@example.com",
        },
    },
    university: {
        eyebrows: {
            name: "University · 1 of 4",
            contact: "University · 2 of 4",
            dept: "University · 3 of 4",
            interest: "University · 4 of 4",
            specialization: "University · 4 of 4",
        },
        headings: {
            name: "Institution name?",
            contact: "Contact person",
            dept: "Department?",
            interest: "Interested in?",
            specialization: "Which specialization?",
        },
        placeholders: {
            name: "University / College name",
            contactName: "Full name",
            designation: "Designation / Role (e.g. HOD, TPO)",
            dept: "e.g. Computer Science, T&P Cell",
            other: "Describe what you're looking for...",
        },
    },
    buttons: {
        continue: "Continue",
        submit: "Submit",
        loading: "Please wait...",
    },
    toasts: {
        success: "Thank you! We'll be in touch 🎉",
        errorGeneric: "Something went wrong",
        alreadyRegistered: "You have already registered with AI.Prof. Our team will reach out to you within the next 24 hours.",
        invalidMobile: "Enter a valid 10-digit mobile number",
        invalidName: "Enter a valid name",
        invalidEmail: "Enter a valid email address",
        enterInstitution: "Enter institution name",
        invalidContactName: "Enter a valid contact name",
        enterDesignation: "Enter designation",
        enterDept: "Enter department",
        selectProgramme: "Select a programme",
        describeInterest: "Describe your interest",
        selectSpecialization: "Select a specialization",
    },
    submitValues: {
        universityOtherInterest: "Other",
        universityPlacementInterest: "Placement Track",
    },
    logoAlt: "AI.Prof",
};

// ============================================================
// ANNOUNCEMENT BANNER on the top of the navbar
// ============================================================
export const ANNOUNCEMENT_BANNER = {
    emoji: "🎓",
    boldText: "Admissions Open",
    text: " — Apply now for the 4 Year Computer Science with Specialization in AI & ML.",
    buttonLabel: "Apply Now",
    dismissAriaLabel: "Dismiss announcement",
};

// ============================================================
// WAITLIST CTA SECTION (This section is hided in the UI)
// ============================================================
export const WAITLIST_CTA = {
    launchDate: "2026-09-01T00:00:00Z",
    eyebrow: "Grand Launch",
    headlineLine1: "The launch is coming. ",
    headlineLine2: "Be first in line.",
    description:
        "Students, universities, and companies who join today get first access, founding member benefits, and a front-row seat to what's coming.",
    countdownLabels: ["Days", "Hours", "Mins", "Secs"],
    claimSpotCta: "Claim My Spot",
    shareCta: "Share With a Friend",
    benefitsHeading: "What Waitlist Members Get",
    benefits: [
        "Early platform access before public launch",
        "Founding cohort discount on all programmes",
        "Free AI Readiness Report (worth ₹1,499)",
        "Priority placement with hiring partners",
        "Exclusive founding community access",
    ],
    shareTitle: "AI Prof",
    shareText:
        "I just joined the AI Prof waitlist. If you care about how India hires tech talent — you should too.",
};

// ============================================================
// CHATBOT UI
// ============================================================
export const CHATBOT_UI = {
    headerTitle: "AI Prof Assistant",
    errorMessages: {
        auth_error: "The chat service isn't configured yet. Please check back soon! 🙏",
        rate_limit: "We're a bit busy right now. Please try again in a moment. 😊",
        llm_not_configured: "The AI service isn't set up yet. Our team is working on it! 🔧",
        server_error: "Something went wrong on our end. Try again or email support@aiprof.com 🙏",
        network_error: "Can't reach the server. Check your connection and try again. 🌐",
        message_too_long: "Your message is too long. Please keep it under 1,000 characters! 😊",
    },
    initialMessages: {
        greeting: "Hi! I'm the AI Prof assistant. 👋",
        help: "Ask me about our programmes, mentorship, placement support, fees, or anything else. I'm here to help!",
    },
    quickReplies: [
        "Tell me about the programmes",
        "How does placement work?",
        "What's the fee structure?",
        "How do I join the waitlist?",
    ],
    inputPlaceholderStreaming: "Waiting for response…",
    inputPlaceholder: "Ask me anything…",
};
